import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Button from '../components/common/ActionButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate, useParams } from 'react-router-dom';

import PageHeader from '../components/PageHeader';
import {
  fetchSkippedTestsAnalysis,
  type SkippedTestsAnalysis,
  type SkippedTestRow,
} from '../api/runs';
import { formatPercentage } from '../utils/percentage';

const formatRiskSignal = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value)
    ? formatPercentage(value * 100)
    : 'N/A';

const formatProbability = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value)
    ? `${(value * 100).toFixed(1)}%`
    : 'Unavailable';

const MetricCard = ({
  label,
  value,
  helper,
}: {
  label: string;
  value: string | number;
  helper?: string;
}) => (
  <Paper
    sx={{
      p: 1.6,
      bgcolor: 'rgba(255,255,255,0.035)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 2,
      minWidth: 0,
      boxShadow: 'none',
    }}
  >
    <Typography
      variant="caption"
      sx={{ color: '#7D8590', textTransform: 'uppercase', fontWeight: 700 }}
    >
      {label}
    </Typography>
    <Typography variant="h6" sx={{ color: '#E6EDF3', fontWeight: 760 }}>
      {value}
    </Typography>
    {helper && (
      <Typography variant="caption" sx={{ color: '#7D8590' }}>
        {helper}
      </Typography>
    )}
  </Paper>
);

const SkippedTestsAnalysisPage = () => {
  const navigate = useNavigate();
  const { runId } = useParams<{ runId: string }>();
  const [data, setData] = useState<SkippedTestsAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [service, setService] = useState('');
  const [riskBucket, setRiskBucket] = useState('');
  const [alwaysGreen, setAlwaysGreen] = useState('');
  const [redundant, setRedundant] = useState('');

  useEffect(() => {
    if (!runId) {
      setError('Missing run id');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    fetchSkippedTestsAnalysis(runId, {
      page,
      page_size: 50,
      service,
      risk_bucket: riskBucket,
      is_always_green:
        alwaysGreen === '' ? undefined : alwaysGreen === 'true',
      is_redundant: redundant === '' ? undefined : redundant === 'true',
    })
      .then(setData)
      .catch((loadError) => {
        setError(loadError?.message || 'Failed to load skipped tests');
      })
      .finally(() => setLoading(false));
  }, [runId, page, service, riskBucket, alwaysGreen, redundant]);

  const rows: SkippedTestRow[] = data?.rows ?? [];
  const summary = data?.summary;
  const activeFilters = [service, riskBucket, alwaysGreen, redundant].filter(Boolean).length;
  const filteredCount =
    data?.pagination.filtered_count ?? data?.pagination.total ?? 0;
  const totalSkipped =
    data?.pagination.total_skipped_count ??
    summary?.total_skipped_count ??
    summary?.skipped_count ??
    0;
  const showingCount = rows.length;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <PageHeader
        icon={<InfoOutlinedIcon fontSize="inherit" />}
        title="Skipped Tests Analysis"
        subtitle="Tests not selected in this saved run. Explanations use its frozen model decisions and mappings; historical stability or name similarity is not presented as an exclusion cause unless the saved decision says so."
      />

      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/results${runId ? `?runId=${runId}` : ''}`)}
        sx={{
          mb: 2,
          textTransform: 'none',
          borderColor: 'rgba(255,255,255,0.16)',
          color: '#E6EDF3',
        }}
      >
        Back to results
      </Button>

      {loading && (
        <Stack alignItems="center" py={6}>
          <CircularProgress sx={{ color: '#39ff14' }} />
        </Stack>
      )}

      {error && !loading && (
        <Paper sx={{ p: 3, bgcolor: '#161B22', color: '#F87171' }}>
          {error}
        </Paper>
      )}

      {data && !loading && !error && (
        <Stack spacing={2.5}>
          <Stack
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                lg: 'repeat(6, minmax(0, 1fr))',
              },
              gap: 1.5,
            }}
          >
            <MetricCard
              label="Not selected"
              value={totalSkipped}
              helper={`${summary?.selected_count ?? 0} selected from ${summary?.total_candidates ?? 0}`}
            />
            {activeFilters > 0 && (
              <MetricCard
                label="Filtered Results"
                value={filteredCount}
                helper={`${activeFilters} active filter${activeFilters === 1 ? '' : 's'}`}
              />
            )}
            <MetricCard
              label="Minimum informativeness for standard selection"
              value={formatProbability(summary?.adaptive_probability_threshold)}
              helper="Coverage requirements can include tests below this value"
            />
            <MetricCard
              label="Selection breadth"
              value={summary?.effective_tau ?? 'Unavailable'}
              helper="Lower values are more restrictive; not a probability cutoff"
            />
            <MetricCard
              label="Near-miss influence"
              value={summary?.near_miss_influence_enabled === true ? 'Enabled' : summary?.near_miss_influence_enabled === false ? 'Disabled' : 'Unavailable'}
              helper={summary?.near_miss_state ? `Saved model state: ${summary.near_miss_state.replace(/_/g, ' ')}` : 'Ordinary runtime remains separate'}
            />
          </Stack>

          <Box component="details" sx={{ '& summary': { cursor: 'pointer', fontWeight: 700 } }}>
            <Box component="summary">Technical and legacy diagnostics</Box>
            <Typography variant="body2">Model: {summary?.model_version ?? 'Unavailable'}; predictor: {summary?.probability_source ?? 'Unavailable'}.</Typography>
            <Typography variant="body2">Legacy p_initial bands among unselected tests — high: {summary?.high_risk_skipped_count ?? 0}, medium: {summary?.medium_risk_skipped_count ?? 0}, low: {summary?.low_risk_skipped_count ?? 0}. These are heuristic score bands, not mapped risk coverage or model probabilities.</Typography>
            <Typography variant="body2">Historically stable flags: {summary?.always_green_avoided_count ?? 0}. Name-similarity flags: {summary?.redundancy_tracking_available ? summary.redundant_tests_avoided_count : 'not tracked'}. These post-decision classifications do not establish why the RF excluded a test or prove behavioral redundancy.</Typography>
          </Box>

          {!summary?.redundancy_tracking_available && (
            <Paper
              sx={{
                p: 1.5,
                bgcolor: 'rgba(255,255,255,0.035)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <Typography variant="body2" sx={{ color: '#C9D1D9' }}>
                Legacy name-similarity classification is not available for this run. It does not establish behavioral equivalence.
              </Typography>
            </Paper>
          )}

          <Paper
            sx={{
              p: { xs: 1.5, md: 2 },
              bgcolor: '#161B22',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 2,
            }}
          >
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Typography variant="body2" sx={{ color: '#C9D1D9' }}>
                  {activeFilters > 0
                    ? `Showing ${showingCount} filtered results out of ${totalSkipped} skipped tests`
                    : `Showing ${showingCount} of ${totalSkipped} skipped tests`}
                </Typography>
                {activeFilters > 0 && (
                  <Typography variant="caption" sx={{ color: '#7D8590' }}>
                    Filtered results: {filteredCount}
                  </Typography>
                )}
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <TextField
                  label="Service"
                  size="small"
                  value={service}
                  onChange={(event) => {
                    setPage(1);
                    setService(event.target.value);
                  }}
                  sx={{ minWidth: 200 }}
                />
                <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Legacy p_initial band</InputLabel>
                  <Select
                    label="Legacy p_initial band"
                    value={riskBucket}
                    onChange={(event) => {
                      setPage(1);
                      setRiskBucket(event.target.value);
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Historically stable flag</InputLabel>
                  <Select
                    label="Historically stable flag"
                    value={alwaysGreen}
                    onChange={(event) => {
                      setPage(1);
                      setAlwaysGreen(event.target.value);
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Name-similarity flag</InputLabel>
                  <Select
                    label="Name-similarity flag"
                    value={redundant}
                    onChange={(event) => {
                      setPage(1);
                      setRedundant(event.target.value);
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              {rows.length === 0 ? (
                <Typography sx={{ color: '#7D8590', py: 3 }}>
                  No skipped tests match the current filters.
                </Typography>
              ) : (
                <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 980 }}>
                    <TableHead>
                      <TableRow
                        sx={{
                          '& th': {
                            color: '#7D8590',
                            textTransform: 'uppercase',
                            fontSize: '0.75rem',
                          },
                        }}
                      >
                        <TableCell>Test</TableCell>
                        <TableCell>Exact service</TableCell>
                        <TableCell align="center">Estimated informativeness</TableCell>
                        <TableCell>Associated risks / severity</TableCell>
                        <TableCell>Saved decision</TableCell>
                        <TableCell>Evidence</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            {row.test_case_identifier || row.test_id}
                          </TableCell>
                          <TableCell>{row.service || 'N/A'}</TableCell>
                          <TableCell align="center">
                            {formatProbability(row.informative_probability)}
                          </TableCell>
                          <TableCell>
                            {row.risk_areas?.length
                              ? row.risk_areas.map(risk => `${risk} (${row.risk_area_criticality?.[risk] ?? 'unknown'})`).join(', ')
                              : 'Mapping unavailable'}
                          </TableCell>
                          <TableCell sx={{ minWidth: 320 }}>
                            {row.business_skip_reason}
                          </TableCell>
                          <TableCell><Box component="details"><Box component="summary">Details</Box>
                            <Typography variant="body2">This test was not selected in the actual saved run. Its estimated informativeness was {formatProbability(row.informative_probability)}; the standard threshold was {formatProbability(row.adaptive_probability_threshold)}.</Typography>
                            {row.comparison && <Typography variant="body2">Without near-miss influence: {formatProbability(row.comparison.probability_without_influence)} and {row.comparison.selected_without_influence ? 'selected' : 'not selected'}. With near-miss influence: {formatProbability(row.informative_probability)} and not selected.</Typography>}
                            <Typography variant="caption" display="block">Saved inclusion rule: {(row.inclusion_rule ?? 'Unavailable').replace(/_/g, ' ')}. Historical stability flag: {row.is_always_green ? 'yes' : 'no'}; legacy name-similarity flag: {row.is_redundant ? 'yes' : 'no'}.</Typography>
                            <Typography variant="caption" display="block">Legacy p_initial signal: {formatRiskSignal(row.dynamic_risk_signal)}; legacy execution value: {formatRiskSignal(row.final_execution_value ?? row.execution_value)}; legacy band: {row.risk_bucket ?? 'Unavailable'}.</Typography>
                          </Box></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {data.pagination.total_pages > 1 && (
                <Stack alignItems="center">
                  <Pagination
                    page={page}
                    count={data.pagination.total_pages}
                    onChange={(_, nextPage) => setPage(nextPage)}
                    color="primary"
                  />
                </Stack>
              )}
            </Stack>
          </Paper>
        </Stack>
      )}
    </Box>
  );
};

export default SkippedTestsAnalysisPage;
